import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import {
    HARVEST_SUMMARY_IMAGE_LAYER,
    HARVEST_SUMMARY_SOURCE,
    PLANTING_SUMMARY_IMAGE_LAYER,
    PLANTING_SUMMARY_SOURCE,
} from "@/lib/map/layers";
import {
    Coordinates,
    invertedTurfBboxToFourCornerCoordinates,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import { ImageSource } from "mapbox-gl";
import {
    HarvestOperation,
    PlantingOperation,
    ListApplications,
    SectorSummary,
} from "@/types/operations";
import {
    faTrash,
    faPlusCircle,
    faFileImport,
    faMap,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Badge,
    Button,
    Card,
    CardBody,
    DateRangePicker,
    DateValue,
    Divider,
    RangeValue,
} from "@heroui/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { parseDate } from "@internationalized/date";
import ApplicationSummary from "@/components/field/operations/ApplicationSummary";
import IrrigationSummary from "@/components/field/operations/IrrigationSummary";
import { roundTo } from "@/lib/utils/numbers";
import { formatInputRates } from "@/lib/utils/operations";
export const Route = createFileRoute("/dashboard/fields/$fieldId/operations/")({
    component: RouteComponent,
});
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { useI18n } from "@/lib/i18n/useI18n";
import useConfirm from "@/lib/confirm/useConfirm";
import { FieldConnection } from "@/types/connection";
import ImportOperation from "@/components/field/operations/ImportOperation";
import useAuth from "@/lib/auth/useAuth";
import dayjs from "dayjs";

function RouteComponent() {
    const navigate = useNavigate();
    const { formatUnit } = useI18n();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { isConsultantOrSentinel } = useAuth();
    const { irrigationEnabled } = useFeatureFlag();
    const { fieldId, selectedCropCycle, fieldQuery, fieldBbox, insightsParams } =
        useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const harvestMapRequest = useMutateRequest<Blob>({ method: "get" });
    const plantingMapRequest = useMutateRequest<Blob>({ method: "get" });
    const { reset: resetHarvestMap } = harvestMapRequest;
    const { reset: resetPlantingMap } = plantingMapRequest;

    useEffect(() => {
        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    HARVEST_SUMMARY_IMAGE_LAYER.id,
                    PLANTING_SUMMARY_IMAGE_LAYER.id,
                ]);
                removeSourcesIfExists(map, [
                    HARVEST_SUMMARY_SOURCE,
                    PLANTING_SUMMARY_SOURCE,
                ]);
            }
            resetHarvestMap();
            resetPlantingMap();
        };
    }, [
        map,
        fieldId,
        selectedCropCycle?.id,
        resetHarvestMap,
        resetPlantingMap,
    ]);

    const harvestOperationsQuery = useGetRequest<HarvestOperation[]>({
        url: `fields/${fieldId}/harvest_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
            crop_zone_id: insightsParams?.crop_zone_id,
        },
    });

    const plantingOperationsQuery = useGetRequest<PlantingOperation[]>({
        url: `fields/${fieldId}/planting_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
            crop_zone_id: insightsParams?.crop_zone_id,
        },
    });

    const [dates, setDates] = useState<RangeValue<DateValue> | null>({
        start: parseDate(
            selectedCropCycle?.start_date ??
                dayjs().subtract(3, "months").format("YYYY-MM-DD"),
        ),
        end: parseDate(
            selectedCropCycle?.end_date ?? dayjs().format("YYYY-MM-DD"),
        ),
    });

    const applicationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: dates?.start?.toString(),
            end_date: dates?.end?.toString(),
            crop_zone_id: insightsParams?.crop_zone_id,
        },
    });

    const sectorSummaryQuery = useGetRequest<SectorSummary[]>({
        url: `fields/${fieldId}/application_operations/sector_summary/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
            crop_zone_id: insightsParams?.crop_zone_id,
        },
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const harvestOperation = useMemo(() => {
        if (selectedCropCycle?.effective_planting_operation?.start_date) {
            const zoneId = selectedCropCycle.crop_zone;
            return harvestOperationsQuery?.data
                ?.filter((op) => {
                    if (
                        op.start_date <
                        selectedCropCycle.effective_planting_operation
                            .start_date
                    ) {
                        return false;
                    }
                    if (zoneId == null) return true;
                    return op.crop_zone == null || op.crop_zone === zoneId;
                })
                .sort((a, b) => (a.start_date > b.start_date ? -1 : 1))[0];
        }
        return null;
    }, [harvestOperationsQuery?.data, selectedCropCycle]);

    const hasDuplicateHarvestOperations = useMemo(() => {
        if (!harvestOperation) return false;
        return (
            (harvestOperationsQuery?.data?.filter(
                (h) =>
                    h.start_date === harvestOperation.start_date &&
                    h.crop_zone === harvestOperation.crop_zone,
            ).length ?? 0) > 1
        );
    }, [harvestOperation, harvestOperationsQuery?.data]);

    const deleteHarvestOperation = useMutateRequest({
        url: harvestOperation
            ? `harvest_operations/${harvestOperation.id}/`
            : "",
        method: "delete",
    });

    const confirm = useConfirm();

    const handleHarvestDelete = () => {
        confirm.showConfirm({
            title: "Delete Operation",
            description: "Are you sure you want to delete this operation?",
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                await deleteHarvestOperation.mutateAsync({});
                resetHarvestMap();
                if (map) {
                    removeLayersIfExists(map, [HARVEST_SUMMARY_IMAGE_LAYER.id]);
                    removeSourcesIfExists(map, [HARVEST_SUMMARY_SOURCE]);
                }
                await harvestOperationsQuery.refetch?.();
            },
        });
    };

    const plantingOperation = useMemo(() => {
        const plantingId = selectedCropCycle?.planting_operation;
        if (plantingId == null || plantingId === -1) return null;
        return (
            plantingOperationsQuery?.data?.find((op) => op.id === plantingId) ??
            (selectedCropCycle?.effective_planting_operation?.id === plantingId
                ? selectedCropCycle.effective_planting_operation
                : null)
        );
    }, [plantingOperationsQuery?.data, selectedCropCycle]);

    const hasDuplicatePlantingOperations = useMemo(() => {
        if (!plantingOperation) return false;
        const cropKey = plantingOperation.crop_configuration?.crop_key;
        return (
            (plantingOperationsQuery?.data?.filter(
                (p) =>
                    p.start_date === plantingOperation.start_date &&
                    p.crop_zone === plantingOperation.crop_zone &&
                    p.crop_configuration?.crop_key === cropKey,
            ).length ?? 0) > 1
        );
    }, [plantingOperation, plantingOperationsQuery?.data]);

    const deletePlantingOperation = useMutateRequest({
        url: plantingOperation
            ? `planting_operations/${plantingOperation.id}/`
            : "",
        method: "delete",
    });

    const handlePlantingDelete = () => {
        confirm.showConfirm({
            title: "Delete Operation",
            description: "Are you sure you want to delete this operation?",
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                await deletePlantingOperation.mutateAsync({});
                resetPlantingMap();
                if (map) {
                    removeLayersIfExists(map, [
                        PLANTING_SUMMARY_IMAGE_LAYER.id,
                    ]);
                    removeSourcesIfExists(map, [PLANTING_SUMMARY_SOURCE]);
                }
                await plantingOperationsQuery.refetch?.();
            },
        });
    };

    useEffect(() => {
        if (!selectedCropCycle) return;

        setDates({
            start: parseDate(selectedCropCycle.start_date),
            end: selectedCropCycle.end_date
                ? parseDate(selectedCropCycle.end_date)
                : (null as unknown as DateValue),
        });
    }, [selectedCropCycle]);

    const { t } = useTranslation("operations");

    const fieldIsIrrigated = useMemo(() => {
        if (!fieldQuery?.data) return false;
        return fieldQuery.data.irrigation_systems.length > 0;
    }, [fieldQuery?.data]);

    const connectionsQuery = useGetRequest<FieldConnection[]>({
        url: "field_connections/",
        enabled: !!fieldId,
        params: {
            field: fieldId,
        },
    });
    const [selectedConnection, setSelectedConnection] =
        useState<FieldConnection | null>(null);

    const showOperationMap = (
        kind: "harvest" | "planting",
        operation: HarvestOperation | PlantingOperation,
    ) => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const request =
            kind === "harvest" ? harvestMapRequest : plantingMapRequest;
        const sourceId =
            kind === "harvest"
                ? HARVEST_SUMMARY_SOURCE
                : PLANTING_SUMMARY_SOURCE;
        const imageLayer =
            kind === "harvest"
                ? HARVEST_SUMMARY_IMAGE_LAYER
                : PLANTING_SUMMARY_IMAGE_LAYER;
        const url = `${kind}_operations/${operation.id}/map_display_image/`;
        if (request.variables?.url === url) {
            request.reset();
            removeLayersIfExists(map, [imageLayer.id]);
            removeSourcesIfExists(map, [sourceId]);
            return;
        }

        if (kind === "harvest") {
            plantingMapRequest.reset();
            removeLayersIfExists(map, [PLANTING_SUMMARY_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [PLANTING_SUMMARY_SOURCE]);
        } else {
            harvestMapRequest.reset();
            removeLayersIfExists(map, [HARVEST_SUMMARY_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [HARVEST_SUMMARY_SOURCE]);
        }

        request.mutate(
            {
                url,
                params: { band: 1 },
                responseType: "blob",
            },
            {
                onSuccess: (image) => {
                    const imageUrl = window.URL.createObjectURL(image);
                    const coordinates =
                        (invertedTurfBboxToFourCornerCoordinates(
                            operation.bounds,
                        ) as Coordinates | null) || fieldBbox;
                    const source = map.getSource<ImageSource>(sourceId);
                    if (source) {
                        source.updateImage({ url: imageUrl, coordinates });
                    } else {
                        map.addSource(sourceId, {
                            type: "image",
                            url: imageUrl,
                            coordinates,
                        });
                        map.addLayer(imageLayer);
                    }
                },
            },
        );
    };

    return (
        <div className="flex flex-col gap-2 p-2 text-sm mb-[50px]">
            {selectedConnection && (
                <ImportOperation
                    connection={selectedConnection}
                    onClose={() => {
                        setSelectedConnection(null);
                        applicationOperationsQuery.refetch?.();
                    }}
                />
            )}
            <div className="flex flex-row items-center gap-2 w-full">
                <Button
                    fullWidth
                    color="secondary"
                    as={Link}
                    to={"/dashboard/fields/$fieldId/operations/add"}
                    radius="sm"
                >
                    <FontAwesomeIcon icon={faPlusCircle} />
                    {t("addOperation")}
                </Button>
                {connectionsQuery.data && connectionsQuery.data.length > 0 && (
                    <Button
                        fullWidth
                        color="secondary"
                        onPress={() =>
                            setSelectedConnection(connectionsQuery.data![0])
                        }
                        radius="sm"
                    >
                        <FontAwesomeIcon icon={faFileImport} /> Import Operation
                    </Button>
                )}
            </div>
            {harvestOperation && (
                <>
                    <div className="flex flex-row items-center justify-between w-full">
                        <Badge
                            color="warning"
                            content=""
                            isInvisible={!hasDuplicateHarvestOperations}
                        >
                            <span className="text-brandgreen-500 font-semibold text-lg">
                                {t("harvest")}
                            </span>
                        </Badge>
                    </div>

                    <Card
                        isPressable
                        onPress={() =>
                            navigate({
                                to: `/dashboard/fields/${fieldId}/operations/harvest/${harvestOperation.id}`,
                            })
                        }
                    >
                        <CardBody>
                            <div className="flex flex-row gap-2 w-full justify-between">
                                <div className="flex flex-col">
                                    <Badge
                                        color="warning"
                                        content="Duplicated"
                                        isInvisible={
                                            !hasDuplicateHarvestOperations
                                        }
                                        size="sm"
                                    >
                                        <div className="font-bold">
                                            {t("date")}
                                        </div>
                                    </Badge>
                                    {harvestOperation.start_date}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">{t("crop")}</div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("yield")}
                                    </div>
                                    {formatInputRates(
                                        harvestOperation.input_rates,
                                        "rate_1",
                                        0,
                                    ) ??
                                        harvestOperation.irrigated_averages
                                            ?.yield ??
                                        harvestOperation.field_averages
                                            ?.yield}{" "}
                                    {t("yieldUnit")}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("moisture")}
                                    </div>
                                    {formatInputRates(
                                        harvestOperation.input_rates,
                                        "rate_2",
                                        0,
                                    ) ??
                                        harvestOperation.irrigated_averages
                                            ?.moisture ??
                                        harvestOperation.field_averages
                                            ?.moisture}
                                    %
                                </div>
                                <div className="flex flex-row gap-1 items-center">
                                    <Button
                                        isIconOnly
                                        color="primary"
                                        variant={
                                            harvestMapRequest.variables?.url ===
                                            `harvest_operations/${harvestOperation.id}/map_display_image/`
                                                ? "solid"
                                                : "bordered"
                                        }
                                        className="tiny-icon-button"
                                        aria-label="View yield map"
                                        onPress={() =>
                                            showOperationMap(
                                                "harvest",
                                                harvestOperation,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMap} />
                                    </Button>
                                    {isConsultantOrSentinel && (
                                        <Button
                                            isIconOnly
                                            variant="solid"
                                            color="danger"
                                            size="sm"
                                            aria-label="Delete Harvest Operation"
                                            onPress={handleHarvestDelete}
                                            isLoading={
                                                deleteHarvestOperation.isPending
                                            }
                                            isDisabled={
                                                deleteHarvestOperation.isPending
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            {plantingOperation && (
                <>
                    <div className="flex flex-row items-center gap-1 justify-between w-full">
                        <Badge
                            color="warning"
                            content=""
                            isInvisible={!hasDuplicatePlantingOperations}
                        >
                            <span className="text-brandgreen-500 font-semibold text-lg">
                                {t("planting")}
                            </span>
                        </Badge>
                    </div>
                    <Card
                        isPressable
                        onPress={() =>
                            navigate({
                                to: `/dashboard/fields/${fieldId}/operations/planting/${plantingOperation.id}`,
                            })
                        }
                    >
                        <CardBody>
                            <div className="flex flex-row flex-wrap gap-2 w-full justify-between">
                                <div className="flex flex-col">
                                    <Badge
                                        color="warning"
                                        content="Duplicated"
                                        isInvisible={
                                            !hasDuplicatePlantingOperations
                                        }
                                        size="sm"
                                    >
                                        <div className="font-bold">
                                            {t("date")}
                                        </div>
                                    </Badge>
                                    {plantingOperation.start_date}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">{t("crop")}</div>
                                    {plantingOperation?.crop_configuration
                                        ?.crop_name ?? "--"}
                                </div>
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("hybrid")}
                                    </div>
                                    {plantingOperation?.varieties?.[0]?.name ??
                                        "--"}
                                </div>
                                {plantingOperation.crop_configuration?.planting_form_fields.includes(
                                    "crm",
                                ) && (
                                    <div className="flex flex-col">
                                        <div className="font-bold">
                                            Maturity (CRM)
                                        </div>
                                        {plantingOperation.varieties?.[0]
                                            ?.crm ?? "--"}
                                    </div>
                                )}
                                <div className="flex flex-col">
                                    <div className="font-bold">
                                        {t("seedingRate")}
                                    </div>
                                    {formatInputRates(
                                        plantingOperation.input_rates,
                                        "rate_1",
                                        0,
                                    ) ??
                                        roundTo(
                                            plantingOperation.average_seeding_rate ??
                                                0,
                                            0,
                                        )}{" "}
                                    {`seeds/${formatUnit("ac", "ha")}`}
                                </div>
                                <div className="flex flex-row gap-1 items-center">
                                    <Button
                                        isIconOnly
                                        color="primary"
                                        variant={
                                            plantingMapRequest.variables
                                                ?.url ===
                                            `planting_operations/${plantingOperation.id}/map_display_image/`
                                                ? "solid"
                                                : "bordered"
                                        }
                                        className="tiny-icon-button"
                                        aria-label="View seeding-rate map"
                                        onPress={() =>
                                            showOperationMap(
                                                "planting",
                                                plantingOperation,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMap} />
                                    </Button>
                                    {isConsultantOrSentinel && (
                                        <Button
                                            isIconOnly
                                            variant="solid"
                                            color="danger"
                                            size="sm"
                                            aria-label="Delete Planting Operation"
                                            onPress={handlePlantingDelete}
                                            isLoading={
                                                deletePlantingOperation.isPending
                                            }
                                            isDisabled={
                                                deletePlantingOperation.isPending
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}
            <Divider className="my-2" />
            <DateRangePicker
                size="sm"
                label={t("startAndEndDate")}
                value={dates}
                aria-label={t("startAndEndDate")}
                onChange={setDates}
            />
            <ApplicationSummary
                fieldId={Number(fieldId!)}
                selectedCropCycle={selectedCropCycle!}
                applicationOperationsQuery={applicationOperationsQuery}
                sectorSummaryQuery={sectorSummaryQuery}
            />
            {irrigationEnabled && fieldIsIrrigated && (
                <IrrigationSummary
                    startDate={dates?.start?.toString()}
                    endDate={dates?.end?.toString()}
                />
            )}
        </div>
    );
}
